/**
 * 2026-04-25 Phase 32-C.1 — 4상태 피날레 이펙트.
 *
 * ForgeOutcome 별로 전혀 다른 연출:
 *   CRIT_SUCCESS → 🌟 금빛 폭발 + 16 광선 방사 + 무지개 glow (2.5s)
 *   SUCCESS      → ✧ 은백 링 한 번 + soft glow (1.2s)
 *   FAIL         → ✕ 재 파편 흩어짐 + 어두워짐 (1s)
 *   CRIT_FAIL    → ☠ 붉은 flash + shake + shatter 32파편 (3s)
 *
 * 초월 대성공 (transcendent=true) 일 때는 CRIT_SUCCESS + 추가 크라운.
 */
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { tokens } from "@/art/tokens";
import type { ForgeOutcome } from "@/api/endpoints/forge";

interface Props {
    outcome: ForgeOutcome;
    /** 어떤 의식의 판결인지에 따라 문구 결을 조금 다르게 준다. */
    kind?: "refine" | "engrave";
    /** 카드 shimmer / 오라와 같은 계열의 보상색. */
    accentColor?: string;
    /** 초월 대성공 (제련 SSSS). CRIT_SUCCESS 와 같이 쓰임. */
    transcendent?: boolean;
    /** 장비 파괴 여부 (대실패 + 제련 구간 3). shatter 연출 추가. */
    destroyed?: boolean;
    /** 연출 끝나면 호출. */
    onComplete?: () => void;
}

export function ForgeOutcomeBurst({ outcome, kind = "refine", accentColor, transcendent, destroyed, onComplete }: Props) {
    const duration = outcomeDuration(outcome, transcendent);

    useEffect(() => {
        const id = setTimeout(() => onComplete?.(), duration);
        return () => clearTimeout(id);
    }, [duration, onComplete]);

    return (
        <div style={overlayStyle} aria-hidden>
            <AnimatePresence>
                <KindBackdrop kind={kind} accentColor={accentColor} />
                {outcome === "CRIT_SUCCESS" && <CritSuccessBurst transcendent={transcendent} kind={kind} accentColor={accentColor} />}
                {outcome === "SUCCESS" && <SuccessRing kind={kind} accentColor={accentColor} />}
                {outcome === "FAIL" && <FailAsh kind={kind} accentColor={accentColor} />}
                {outcome === "CRIT_FAIL" && <CritFailShatter destroyed={destroyed} kind={kind} accentColor={accentColor} />}
            </AnimatePresence>
        </div>
    );
}

function KindBackdrop({ kind, accentColor }: { kind: "refine" | "engrave"; accentColor?: string }) {
    const tone = accentColor ?? (kind === "engrave" ? tokens.color.moss : tokens.color.brass);
    if (kind === "engrave") {
        return (
            <>
                <motion.div
                    initial={{ opacity: 0, scale: 0.92 }}
                    animate={{ opacity: [0.08, 0.16, 0.08], scale: [0.92, 1.03, 0.98] }}
                    transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                        position: "absolute",
                        width: 420,
                        height: 420,
                        borderRadius: "50%",
                        border: `1px solid ${tone}22`,
                        boxShadow: `0 0 36px ${tone}11 inset`,
                    }}
                />
                <motion.div
                    initial={{ opacity: 0, scale: 0.82, rotate: -12 }}
                    animate={{ opacity: [0.06, 0.12, 0.06], scale: [0.82, 0.96, 0.9], rotate: 8 }}
                    transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                        position: "absolute",
                        width: 300,
                        height: 300,
                        borderRadius: "50%",
                        border: `1px dashed ${tone}28`,
                    }}
                />
            </>
        );
    }
    return (
        <>
            <motion.div
                initial={{ opacity: 0, scaleY: 0.9 }}
                animate={{ opacity: [0.06, 0.14, 0.06], scaleY: [0.9, 1.04, 0.96] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                style={{
                    position: "absolute",
                    width: 320,
                    height: 460,
                    borderRadius: 18,
                    background: `linear-gradient(180deg, transparent 0%, ${tone}10 18%, transparent 28%, ${tone}08 46%, transparent 58%, ${tone}10 78%, transparent 100%)`,
                    filter: "blur(1px)",
                }}
            />
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: [0.04, 0.1, 0.04] }}
                transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
                style={{
                    position: "absolute",
                    width: 220,
                    height: 480,
                    borderLeft: `1px solid ${tone}22`,
                    borderRight: `1px solid ${tone}16`,
                }}
            />
        </>
    );
}

function outcomeDuration(o: ForgeOutcome, transcendent?: boolean): number {
    if (o === "CRIT_SUCCESS" && transcendent) return 4000;
    switch (o) {
        case "CRIT_SUCCESS": return 2500;
        case "SUCCESS": return 1200;
        case "FAIL": return 1000;
        case "CRIT_FAIL": return 3000;
    }
}

// ──────── Crit success ────────

function CritSuccessBurst({ transcendent, kind, accentColor }: { transcendent?: boolean; kind: "refine" | "engrave"; accentColor?: string }) {
    const successColor = accentColor ?? tokens.color.brass;
    const rays = Array.from({ length: 16 }, (_, i) => i * (360 / 16));
    return (
        <>
            {kind === "engrave" && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.9, rotate: -8 }}
                    animate={{ opacity: [0, 0.9, 0], scale: [0.9, 1.06, 1.16], rotate: 4 }}
                    transition={{ duration: 1.8, ease: "easeOut" }}
                    style={{
                        position: "absolute",
                        width: 260,
                        height: 260,
                        borderRadius: "50%",
                        border: `1px solid ${successColor}66`,
                        boxShadow: `0 0 32px ${successColor}33`,
                    }}
                />
            )}
            {/* 중앙 빛 폭발 */}
            <motion.div
                initial={{ scale: 0, opacity: 1 }}
                animate={{ scale: 3, opacity: 0 }}
                transition={{ duration: 1.2, ease: "easeOut" }}
                style={{
                    position: "absolute",
                    width: 120, height: 120,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${successColor} 0%, transparent 70%)`,
                    boxShadow: `0 0 120px 60px ${successColor}`,
                }}
            />

            {/* 16 광선 방사 */}
            {rays.map(angle => (
                <motion.div
                    key={angle}
                    initial={{ scale: 0, opacity: 1 }}
                    animate={{ scale: 1, opacity: 0 }}
                    transition={{ duration: 2, ease: "easeOut", delay: 0.1 }}
                    style={{
                        position: "absolute",
                        width: 3,
                        height: 400,
                        background: `linear-gradient(to top, transparent, ${successColor})`,
                        transformOrigin: "center center",
                        rotate: `${angle}deg`,
                        marginLeft: -1.5,
                        marginTop: -200,
                    }}
                />
            ))}

            {/* 중앙 텍스트 */}
            <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                style={centerTextStyle(successColor)}
            >
                {transcendent ? (
                    <>
                        <div style={{ fontSize: 20, opacity: 0.92, letterSpacing: 4 }}>초월</div>
                        <div style={{ fontSize: 48, textShadow: `0 0 30px ${successColor}` }}>
                            ✦✦ 네 번의 완성
                        </div>
                        <div style={{ fontSize: 17, marginTop: 10, opacity: 0.88 }}>
                            {kind === "engrave"
                                ? "오늘 붙잡은 이름은 장부의 첫 줄에 남습니다"
                                : "오늘의 망치질은 장부의 맨 앞에 남습니다"}
                        </div>
                    </>
                ) : (
                    <>
                        <div style={{ fontSize: 48, textShadow: `0 0 30px ${successColor}` }}>
                            ✦ 대성공
                        </div>
                        <div style={{ fontSize: 17, marginTop: 10, opacity: 0.88 }}>
                            {kind === "engrave"
                                ? "무기가 이 이름을 끝내 깊게 받아들였습니다"
                                : "장비가 이 결을 끝내 버텨 냈습니다"}
                        </div>
                    </>
                )}
            </motion.div>

            {/* 금빛 상승 파티클 */}
            {Array.from({ length: 20 }).map((_, i) => {
                const x = (Math.random() - 0.5) * 400;
                const delay = Math.random() * 1.5;
                return (
                    <motion.div
                        key={`p-${i}`}
                        initial={{ y: 100, x, opacity: 0 }}
                        animate={{ y: -400, opacity: [0, 1, 0] }}
                        transition={{ duration: 2.5, delay, ease: "easeOut" }}
                        style={{
                            position: "absolute",
                            width: 4, height: 4,
                            borderRadius: "50%",
                                background: successColor,
                                boxShadow: `0 0 8px ${successColor}`,
                        }}
                    />
                );
            })}
        </>
    );
}

// ──────── Success ────────

function SuccessRing({ kind, accentColor }: { kind: "refine" | "engrave"; accentColor?: string }) {
    const successColor = accentColor ?? tokens.color.moss;
    return (
        <>
            {kind === "engrave" && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: [0, 0.7, 0], scale: [0.85, 1.08, 1.18] }}
                    transition={{ duration: 1.1, ease: "easeOut" }}
                    style={{
                        position: "absolute",
                        width: 120,
                        height: 120,
                        borderRadius: "50%",
                        border: `1px dashed ${successColor}`,
                        boxShadow: `0 0 28px ${successColor}33`,
                    }}
                />
            )}
            <motion.div
                initial={{ scale: 0, opacity: 1 }}
                animate={{ scale: 2.5, opacity: 0 }}
                transition={{ duration: 1, ease: "easeOut" }}
                style={{
                    position: "absolute",
                    width: 200, height: 200,
                    borderRadius: "50%",
                    border: `3px solid ${successColor}`,
                    boxShadow: `0 0 40px ${successColor}`,
                }}
            />
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                style={centerTextStyle(successColor)}
            >
                <div style={{ fontSize: 32 }}>✧ 성공</div>
                <div style={{ fontSize: 16, marginTop: 8, opacity: 0.86 }}>
                    {kind === "engrave" ? "이름이 무리 없이 잠겼습니다" : "이번 결은 무리 없이 장비에 눌렸습니다"}
                </div>
            </motion.div>
        </>
    );
}

// ──────── Fail ────────

function FailAsh({ kind, accentColor }: { kind: "refine" | "engrave"; accentColor?: string }) {
    const failColor = kind === "engrave" ? (accentColor ?? tokens.color.moss) : "#999";
    return (
        <>
            {kind === "engrave" && (
                <motion.div
                    initial={{ opacity: 0.35, scale: 0.9 }}
                    animate={{ opacity: 0, scale: 1.2 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    style={{
                        position: "absolute",
                        width: 180,
                        height: 180,
                        borderRadius: "50%",
                        border: `1px solid ${failColor}44`,
                    }}
                />
            )}
            {/* 재 파편 12개 방사 */}
            {Array.from({ length: 12 }).map((_, i) => {
                const angle = (i / 12) * Math.PI * 2;
                const dist = 80 + Math.random() * 80;
                return (
                    <motion.div
                        key={i}
                        initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                        animate={{
                            x: Math.cos(angle) * dist,
                            y: Math.sin(angle) * dist + 60,  // 중력
                            opacity: 0,
                            scale: 0.3,
                        }}
                        transition={{ duration: 0.9, ease: "easeOut" }}
                        style={{
                            position: "absolute",
                            width: 6, height: 6,
                            borderRadius: 1,
                            background: failColor,
                            boxShadow: `0 0 6px ${failColor}44`,
                        }}
                    />
                );
            })}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                style={centerTextStyle(failColor)}
            >
                <div style={{ fontSize: 26 }}>✕ 실패</div>
                <div style={{ fontSize: 15, marginTop: 8, opacity: 0.8 }}>
                    {kind === "engrave"
                        ? "이름은 스쳤지만 아직 무기 안에 머물지 못했습니다"
                        : "이번 흔들림은 아직 장비에 남지 못했습니다"}
                </div>
            </motion.div>
        </>
    );
}

// ──────── Crit fail ────────

function CritFailShatter({ destroyed, kind, accentColor }: { destroyed?: boolean; kind: "refine" | "engrave"; accentColor?: string }) {
    // 2026-04-25 Phase 32-C.5: 대실패 연출 강화 — shake 진폭 ↑, 지속시간 ↑, 정적 한 박자 후 재도 한번.
    const [shaken, setShaken] = useState(true);
    useEffect(() => { setTimeout(() => setShaken(false), 700); }, []);
    const failColor = kind === "engrave" ? (accentColor ?? tokens.color.burgundy) : tokens.color.burgundy;

    return (
        <>
            {kind === "engrave" && !destroyed && (
                <motion.div
                    initial={{ opacity: 0.25, scale: 0.9, rotate: 0 }}
                    animate={{ opacity: [0.2, 0.55, 0], scale: [0.9, 1.08, 1.12], rotate: 12 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    style={{
                        position: "absolute",
                        width: 220,
                        height: 220,
                        borderRadius: "50%",
                        border: `1px solid ${failColor}77`,
                        boxShadow: `0 0 26px ${failColor}33`,
                    }}
                />
            )}
            {/* 붉은 flash — 더 강하게, 두 번 펄스 */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.85, 0.2, 0.5, 0] }}
                transition={{ duration: 0.7, times: [0, 0.1, 0.35, 0.5, 1] }}
                style={{
                    position: "absolute",
                    inset: "-50%",
                    background: failColor,
                    width: "200%", height: "200%",
                }}
            />

            {/* 흔들리는 컨테이너 — 진폭 ±16px, 2 사이클 */}
            <motion.div
                animate={shaken ? {
                    x: [-16, 16, -14, 12, -10, 8, -6, 4, 0],
                    y: [-8, 8, -6, 5, -3, 0, 0, 0, 0],
                } : { x: 0, y: 0 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                style={{ position: "relative" }}
            >
                {/* 장비 파편 */}
                {destroyed && Array.from({ length: 32 }).map((_, i) => {
                    const angle = (i / 32) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
                    const dist = 150 + Math.random() * 200;
                    return (
                        <motion.div
                            key={i}
                            initial={{ x: 0, y: 0, opacity: 1, scale: 1.5 }}
                            animate={{
                                x: Math.cos(angle) * dist,
                                y: Math.sin(angle) * dist + 200,  // 중력 더 세게
                                opacity: 0,
                                scale: 0.3,
                                rotate: Math.random() * 720,
                            }}
                            transition={{ duration: 1.5, ease: "easeOut" }}
                            style={{
                                position: "absolute",
                                width: 8 + Math.random() * 12,
                                height: 2 + Math.random() * 4,
                                background: failColor,
                                borderRadius: 1,
                                boxShadow: `0 0 4px ${failColor}`,
                            }}
                        />
                    );
                })}

                <motion.div
                    initial={{ opacity: 0, scale: 1.2 }}
                    animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                style={centerTextStyle(failColor)}
            >
                <div style={{ fontSize: 48, textShadow: `0 0 20px ${failColor}` }}>
                        ☠ 대실패
                </div>
                    <div style={{ fontSize: 17, marginTop: 12, opacity: 0.9, maxWidth: 420, lineHeight: 1.5 }}>
                        {destroyed
                            ? "이 무기는 오늘의 무게를 버티지 못하고 산산이 흩어졌습니다"
                            : kind === "engrave"
                                ? "이 이름은 끝내 머물지 못했고, 무기에는 깊은 금만 남았습니다"
                                : "이번 망치질은 지나치게 깊었고, 장비에는 날 선 금만 남았습니다"}
                    </div>
                </motion.div>
            </motion.div>
        </>
    );
}

// ──────── 공통 스타일 ────────

const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0,0,0,0.85)",
    zIndex: 2000,
    pointerEvents: "none",
    overflow: "hidden",
};

function centerTextStyle(color: string): React.CSSProperties {
    return {
        position: "absolute",
        textAlign: "center",
        color,
        fontWeight: "bold",
        fontFamily: "serif",
        letterSpacing: 2,
        padding: 20,
        borderRadius: 8,
        textShadow: "0 2px 12px rgba(14,12,24,0.95)",
    };
}
